import React, {
  FC,
  ReactNode,
  ReactText,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Alert, Dropdown, MenuProps, Radio } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { PaginationConfig } from 'antd/lib/pagination';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import type { TooltipContentProps } from 'recharts';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  OptionProps,
  ProjectDurationBoxPlotPoint,
  ProjectDurationDistributionBucket,
  ProjectDurationMetric,
  ProjectDurationReportResponse,
  ProjectDurationRow,
  ProjectDurationTrendPoint,
  ReportKey,
  ReportsFilters,
} from '@models/interfaces';
import { CustomTable } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import type {
  ProjectDurationAverageByGroupChartItem,
  ReportsSummaryMetric,
} from '../../models';
import {
  formatDurationDays,
  FULL_FILTER_CAPTION,
  getProjectDurationAverageByGroupChartData,
  getProjectDurationAverageByGroupTitle,
  getProjectDurationColumns,
  getProjectDurationReportRowClassName,
  getProjectDurationReportRowKey,
  getProjectDurationRowRedirectUrl,
  hasProjectDurationAverageByGroupData,
  hasProjectDurationBoxPlotData,
  hasProjectDurationDistributionData,
  hasProjectDurationTrendData,
  isInteractiveRowClick,
  openReportRowInNewTab,
  PROJECT_DURATION_CHART_COLORS,
  PROJECT_DURATION_DEFAULT_METRIC,
  PROJECT_DURATION_METRIC_LABELS,
  PROJECT_DURATION_METRIC_OPTIONS,
} from '../../models';
import { useReportExport } from '../../models/use-report-export';
import { ReportChartCard } from '../report-chart-card';
import { ReportsSummary } from '../reports-summary';

import './styles.scss';
import themeColors from 'styles/theme/colors.module.scss';

interface ProjectDurationReportProps {
  data?: ProjectDurationReportResponse;
  filters: ReportsFilters;
  groupByOptions: OptionProps[];
  onMetricChange: (metric: ProjectDurationMetric) => void;
  onTableChange: (
    pagination: PaginationConfig,
    tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    extra: TableCurrentDataSource<Store>,
  ) => void;
  sortByOptions: OptionProps[];
}

const CHART_GRID_STROKE = themeColors.chartGrid;
const CHART_AXIS_STROKE = themeColors.fontColor;

const getTooltipPayload = <T,>({
  active,
  payload,
}: TooltipContentProps): T | undefined =>
  active && payload?.length ? (payload[0]?.payload as T) : undefined;

const renderDistributionTooltip = (props: TooltipContentProps): ReactNode => {
  const bucket = getTooltipPayload<ProjectDurationDistributionBucket>(props);

  if (!bucket) {
    return null;
  }

  return (
    <div className="prov-project-duration-report__tooltip">
      <strong>{bucket.label}</strong>
      <span>{`Min Days: ${formatDurationDays(bucket.minDays)}`}</span>
      <span>{`Max Days: ${formatDurationDays(bucket.maxDays)}`}</span>
      <span>{`Projects: ${bucket.projectCount}`}</span>
    </div>
  );
};

const getTrendTooltipRenderer =
  (metricLabel: string) =>
  (props: TooltipContentProps): ReactNode => {
    const point = getTooltipPayload<ProjectDurationTrendPoint>(props);

    if (!point) {
      return null;
    }

    return (
      <div className="prov-project-duration-report__tooltip">
        <strong>{point.period}</strong>
        <span>
          {`Average ${metricLabel}: ${formatDurationDays(
            point.averageDurationDays,
          )}`}
        </span>
        <span>{`Projects: ${point.projectCount}`}</span>
      </div>
    );
  };

const getAverageByGroupTooltipRenderer =
  (metricLabel: string) =>
  (props: TooltipContentProps): ReactNode => {
    const point =
      getTooltipPayload<ProjectDurationAverageByGroupChartItem>(props);

    if (!point) {
      return null;
    }

    return (
      <div className="prov-project-duration-report__tooltip">
        <strong>{point.groupLabel}</strong>
        <span>
          {`Average ${metricLabel}: ${formatDurationDays(
            point.averageDurationDays,
          )}`}
        </span>
        <span>{`Projects: ${point.projectCount}`}</span>
      </div>
    );
  };

export const ProjectDurationReport: FC<ProjectDurationReportProps> = ({
  data,
  filters,
  groupByOptions,
  onMetricChange,
  onTableChange,
  sortByOptions,
}: ProjectDurationReportProps) => {
  const chartsRef = useRef<HTMLDivElement>(null);
  const metric =
    data?.summary?.metric ?? filters.metric ?? PROJECT_DURATION_DEFAULT_METRIC;
  const metricLabel = PROJECT_DURATION_METRIC_LABELS[metric];
  const startAfterEndCount = data?.summary?.startAfterEndCount ?? 0;
  const renderTrendTooltip = useMemo(
    () => getTrendTooltipRenderer(metricLabel),
    [metricLabel],
  );
  const renderAverageByGroupTooltip = useMemo(
    () => getAverageByGroupTooltipRenderer(metricLabel),
    [metricLabel],
  );
  const rows = data?.table?.items ?? [];
  const total = data?.table?.total ?? 0;
  const distribution = data?.charts?.distribution ?? [];
  const trend = data?.charts?.trend ?? [];
  const averageByGroup = data?.charts?.averageByGroup ?? [];
  const boxPlotByProjectType = data?.charts?.boxPlotByProjectType ?? [];
  const averageByGroupChartData = useMemo(
    () => getProjectDurationAverageByGroupChartData(averageByGroup),
    [averageByGroup],
  );
  const averageByGroupTitle = getProjectDurationAverageByGroupTitle(
    filters.groupBy,
    groupByOptions,
    averageByGroup[0]?.groupBy,
  );
  const columns = useMemo(
    () => getProjectDurationColumns(filters, sortByOptions),
    [filters, sortByOptions],
  );
  const metrics: ReportsSummaryMetric[] = [
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Total Projects',
      value: data?.summary?.totalProjects ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: `Average ${metricLabel}`,
      value: formatDurationDays(data?.summary?.averageDurationDays),
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: `Median ${metricLabel}`,
      value: formatDurationDays(data?.summary?.medianDurationDays),
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: `Min ${metricLabel}`,
      value: formatDurationDays(data?.summary?.minDurationDays),
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: `Max ${metricLabel}`,
      value: formatDurationDays(data?.summary?.maxDurationDays),
    },
  ];

  const onRowClick = useCallback(
    (
      record: ProjectDurationRow,
      _newTab: boolean,
      event: { target?: EventTarget | null },
    ): void => {
      const redirectUrl = getProjectDurationRowRedirectUrl(record);

      if (!redirectUrl || isInteractiveRowClick(event)) {
        return;
      }

      openReportRowInNewTab(redirectUrl);
    },
    [],
  );

  const { exporting, exportError, onExportExcel, onExportPdf } =
    useReportExport({
      columns,
      metrics,
      rows,
      chartsRef,
      reportTitle: 'Project Duration',
      scope: {
        page: filters.page ?? 1,
        size: filters.size ?? 25,
        total,
        filters,
      },
    });

  const exportMenu: MenuProps = {
    items: [
      { key: 'excel', label: 'Export as Excel', onClick: onExportExcel },
      { key: 'pdf', label: 'Export as PDF', onClick: onExportPdf },
    ],
  };

  return (
    <div className="prov-project-duration-report">
      <div className="prov-project-duration-report__toolbar">
        <p className="prov-project-duration-report__description">
          Shows how long each project took: start = first date the status became
          Active (create date for projects that existed before status tracking),
          end = Site Acq Complete actual date. Paused Days = time in Hold or
          Suspended; Providence Days = Project Days − Paused Days. The date
          filter narrows by Site Acq Complete date only.
        </p>
        <Dropdown
          disabled={!rows.length || exporting}
          menu={exportMenu}
          trigger={['click']}
        >
          <PrimaryButton
            className="prov-project-duration-report__export"
            title={exporting ? 'Exporting…' : 'Export current page ▾'}
          />
        </Dropdown>
      </div>
      <Radio.Group
        buttonStyle="solid"
        className="prov-project-duration-report__metric-toggle"
        onChange={event =>
          onMetricChange(event.target.value as ProjectDurationMetric)
        }
        optionType="button"
        options={PROJECT_DURATION_METRIC_OPTIONS}
        value={filters.metric ?? metric}
      />

      {exportError && <Alert type="error" message={exportError} />}
      {startAfterEndCount > 0 && (
        <Alert
          className="prov-project-duration-report__start-after-end-note"
          message={`${startAfterEndCount} project${
            startAfterEndCount === 1 ? ' has' : 's have'
          } a start date after the Site Acq Complete actual date. They are marked in the table but excluded from the summary and charts. Check their Project Assigned and Site Acq Complete dates.`}
          showIcon
          type="warning"
        />
      )}
      <ReportsSummary metrics={metrics} />

      <div className="prov-project-duration-report__charts" ref={chartsRef}>
        <ReportChartCard
          hasData={hasProjectDurationDistributionData(distribution)}
          title={`${metricLabel} Distribution`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={distribution}
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="label" stroke={CHART_AXIS_STROKE} />
              <YAxis allowDecimals={false} stroke={CHART_AXIS_STROKE} />
              <Tooltip content={renderDistributionTooltip} />
              <Bar
                dataKey="projectCount"
                fill={PROJECT_DURATION_CHART_COLORS.distribution}
                name="Projects"
              />
            </BarChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={hasProjectDurationTrendData(trend)}
          title={`Monthly Average ${metricLabel}`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={trend}
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="period" stroke={CHART_AXIS_STROKE} />
              <YAxis stroke={CHART_AXIS_STROKE} />
              <Tooltip content={renderTrendTooltip} />
              <Line
                dataKey="averageDurationDays"
                name={`Average ${metricLabel}`}
                stroke={PROJECT_DURATION_CHART_COLORS.trend}
                strokeWidth={2}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={hasProjectDurationAverageByGroupData(averageByGroup)}
          title={averageByGroupTitle}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={averageByGroupChartData}
              layout="vertical"
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis stroke={CHART_AXIS_STROKE} type="number" />
              <YAxis
                dataKey="groupLabel"
                stroke={CHART_AXIS_STROKE}
                type="category"
                width={132}
              />
              <Tooltip content={renderAverageByGroupTooltip} />
              <Bar
                dataKey="averageDurationDays"
                fill={PROJECT_DURATION_CHART_COLORS.averageByGroup}
                name={`Average ${metricLabel}`}
              />
            </BarChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={hasProjectDurationBoxPlotData(boxPlotByProjectType)}
          title={`Project Type ${metricLabel} Quartiles`}
        >
          <div className="prov-project-duration-report__box-plot-table-wrap">
            <table className="prov-project-duration-report__box-plot-table">
              <thead>
                <tr>
                  <th>Project Type</th>
                  <th>Min</th>
                  <th>Q1</th>
                  <th>Median</th>
                  <th>Q3</th>
                  <th>Max</th>
                  <th>Projects</th>
                </tr>
              </thead>
              <tbody>
                {boxPlotByProjectType.map(
                  (point: ProjectDurationBoxPlotPoint, index: number) => (
                    <tr
                      key={
                        point.projectTypeId || point.projectType || `${index}`
                      }
                    >
                      <th scope="row">{point.projectType || 'Unassigned'}</th>
                      <td>{formatDurationDays(point.minDurationDays)}</td>
                      <td>{formatDurationDays(point.q1DurationDays)}</td>
                      <td>{formatDurationDays(point.medianDurationDays)}</td>
                      <td>{formatDurationDays(point.q3DurationDays)}</td>
                      <td>{formatDurationDays(point.maxDurationDays)}</td>
                      <td>{point.projectCount}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </ReportChartCard>
      </div>

      <CustomTable
        module="reports"
        subModule={ReportKey.ProjectDuration}
        className="prov-project-duration-report__table"
        columns={columns}
        dataSource={rows}
        onChange={onTableChange}
        onRowClick={onRowClick}
        pagination={{
          current: filters.page,
          pageSize: filters.size,
          total,
        }}
        rowClassName={getProjectDurationReportRowClassName}
        rowKey={getProjectDurationReportRowKey}
        scroll={{ x: 3000, y: 'auto' }}
      />
    </div>
  );
};
